import { Spin, Table as AntDTable, message } from 'antd';
import type { TablePaginationConfig } from 'antd/es/table';
import type { SorterResult, TableCurrentDataSource } from 'antd/es/table/interface';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import apiClient from 'utils/apiClient';
import qs from 'qs';
import moment from 'moment';
import { config } from 'config/config';
import Switch from 'components/Form/Switch';
import { useIntl } from 'react-intl';
import generalHelpers from 'utils/generalHelpers';
import { useTableColumnsContext } from 'context/TableColumnsContext';

interface Params {
    pagination?: TablePaginationConfig;
}

type AnyRecord = Record<string, any>;

type BaseProps = {
    tableKey: string;
    columns: any[];
    url: string;

    setReload: (v: boolean) => void;
    reload: boolean;

    rowKey?: string | ((record: AnyRecord) => string);
    selectedRowKeys?: React.Key[];
    setSelectedRowKeys?: (keys: React.Key[]) => void;

    saveData?: (data: any) => void;
    rowSelection?: boolean;
    setTotalRecords?: (n: number) => void;
    silentReload?: boolean;
    showTotalResults?: boolean;
    isLoading?: boolean;

    manualDataUpdate?: boolean;
    updateData?: any;

    selectionProps?: AnyRecord;
    onLoadFunction?: false | ((resp: any) => void);

    search?: string;
    searchKey?: string;
    useFilters?: boolean;
    filter?: string | AnyRecord;

    components?: AnyRecord;

    learningProgramCustomColumns?: boolean;
    customFields?: Array<{
        title: string;
        field_type: 'SWITCH' | 'DATE' | 'MULTIPLE_CHOICE' | 'HYPERLINK' | string;
        uuid: string;
    }>;
    hasCustomSorter?: boolean;
    hasCustomFields?: boolean;
    defaultFields?: string[];

    setColumns?: (cols: any[]) => void;

    fetchOnLoad?: boolean;

    additionalSelect?: (v: any) => void;
    additionalSelectKey?: string;
    wholeRow?: boolean;
} & AnyRecord;

type TableProps =
    | (BaseProps & {
          additionalSelect?: undefined;
          wholeRow?: boolean;
          additionalSelectKey?: undefined;
      })
    | (BaseProps & {
          additionalSelect: (v: any) => void;
          wholeRow: true;
          additionalSelectKey?: undefined;
      })
    | (BaseProps & {
          additionalSelect: (v: any) => void;
          wholeRow?: false;
          additionalSelectKey: string;
      });

const QTable: React.FC<TableProps> = ({
    tableKey,
    columns,
    url,
    setReload,
    reload,
    rowKey = 'id',
    selectedRowKeys = [],
    setSelectedRowKeys,
    saveData,
    rowSelection = true,
    setTotalRecords,
    silentReload = false,
    showTotalResults = false,
    isLoading = false,
    manualDataUpdate = false,
    updateData,
    selectionProps = {},
    onLoadFunction = false,
    search = '',
    searchKey = 'q',
    useFilters = false,
    filter,
    components,
    learningProgramCustomColumns,
    customFields = [],
    hasCustomSorter = false,
    defaultFields = [],
    setColumns,
    fetchOnLoad = true,
    additionalSelect,
    additionalSelectKey,
    wholeRow = false,
    ...restProps
}) => {
    const intl = useIntl();
    const { displayColumns } = useTableColumnsContext();

    const showTotal = (total: number, range: number[]) =>
        `${range[0]}-${range[1]} ${intl.formatMessage({ id: 'general.of' })} ${total}`;

    const pageSizeOptions = config.api.environment === 'development' ? [1, 10, 30, 50, 100] : [10, 30];
    const defaultPageSize = 30;

    const [data, setData] = useState<any[] | undefined>();
    const [tableColumns, setTableColumns] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [displayTable, setDisplayTable] = useState(false);

    const [pagination, setPagination] = useState<TablePaginationConfig>({
        showTotal: showTotalResults ? showTotal : undefined,
        current: 1,
        pageSize: defaultPageSize,
        pageSizeOptions: pageSizeOptions.map(String),
        showSizeChanger: true,
        showQuickJumper: true,
    });

    const requestIdRef = useRef(0);

    const handleColumnDisplay = (cols: any[]) => {
        if (displayColumns[tableKey] && displayColumns[tableKey].length) {
            return cols.filter((c: any) => displayColumns[tableKey].includes(c.key));
        }
        return cols;
    };

    const onSelectChange = (newSelectedRowKeys: React.Key[], selectedRows: any[]) => {
        if (additionalSelect) {
            if (wholeRow) {
                additionalSelect(selectedRows);
            } else if (additionalSelectKey) {
                additionalSelect(generalHelpers.pluck(selectedRows, additionalSelectKey));
            } else if (typeof rowKey === 'string') {
                additionalSelect(generalHelpers.pluck(selectedRows, rowKey));
            } else {
                additionalSelect(newSelectedRowKeys);
            }
        }

        setSelectedRowKeys?.(newSelectedRowKeys);
    };

    function normalizeForQuery(input: any): any {
        if (input == null) return input;

        if (moment.isMoment(input)) return input.toISOString();

        if (input instanceof Date) return input.toISOString();

        if (Array.isArray(input)) return input.map(normalizeForQuery);

        if (typeof input === 'object') {
            const out: Record<string, any> = {};
            for (const [k, v] of Object.entries(input)) {
                const nv = normalizeForQuery(v);
                if (nv !== undefined) out[k] = nv;
            }
            return out;
        }

        return input;
    }

    const fetchData = async (params: Params & {sorter?: SorterResult<any> | SorterResult<any>[]} = {}) => {
        const requestId = ++requestIdRef.current;
        if (!silentReload) setLoading(true);

        const current = params.pagination?.current ?? pagination.current ?? 1;
        const pageSize = params.pagination?.pageSize ?? pagination.pageSize ?? defaultPageSize;

        const query: Record<string, any> = { page: current, size: pageSize };

        const sorter = params.sorter as SorterResult<any>;
        if (sorter && sorter.field && sorter.order) {
            query.orderBy = `${sorter.order === 'ascend' ? 'asc' : 'desc'}`;
            query.sortBy = sorter.field;
        }

        if (search) {
            if (useFilters) {
                query.filter = { ...(query.filter || {}), [searchKey]: search };
            } else {
                query[searchKey] = search;
            }
        }

        if (filter) {
            if (typeof filter === 'object') {
            query.filter = { ...(query.filter || {}), ...normalizeForQuery(filter) };
            } else {
            const parsed = qs.parse(filter, { ignoreQueryPrefix: true });
            const norm = normalizeForQuery(parsed);
            if (norm && typeof norm === 'object' && 'filter' in (norm as any)) {
                query.filter = { ...(query.filter || {}), ...(norm as any).filter };
            } else {
                query.filter = { ...(query.filter || {}), ...(norm as any) };
            }
            }
        }

        const requestUrl = `${url}?${qs.stringify(query, {
            encodeValuesOnly: true,
            arrayFormat: 'indices',
        })}`;

        try {
            const response = await apiClient.request(requestUrl, [], 'GET');
            if (requestId !== requestIdRef.current) return; // superseded by a newer fetch

            const total: number | undefined = response?.pagination?.total;

            if (typeof total === 'number' && total > 0) {
                const lastPage = Math.max(1, Math.ceil(total / pageSize));
                if (current > lastPage) {
                    fetchData({ ...params, pagination: { ...params.pagination, current: lastPage, pageSize } });
                    return;
                }
            }

            onLoadFunction && onLoadFunction(response);

            if (defaultFields.length && response.data) {
                await handleCustomColumns(response.data);
            }

            setData(response?.data ?? []);
            saveData?.(response?.data);

            setPagination((prev) => ({
                ...prev,
                ...params.pagination,
                current,
                pageSize,
                showTotal: showTotalResults ? showTotal : undefined,
                ...(typeof total === 'number' ? { total } : {}),
            }));

            if (typeof total === 'number') {
                setTotalRecords?.(total);
            }
        } catch (err: any) {
            console.error(err);

            // Validation failures come through as { status: 422, message: '<json of field => [keys]>' }
            // (apiClient.parsedError); surface the first key instead of a silently empty table.
            if (err?.status === 422) {
                let firstError: string | undefined;

                try {
                    firstError = Object.values(JSON.parse(err.message)).flat()[0] as string | undefined;
                } catch {
                    firstError = typeof err.message === 'string' ? err.message : undefined;
                }

                message.error(
                    firstError
                        ? intl.formatMessage({ id: firstError, defaultMessage: firstError })
                        : intl.formatMessage({ id: 'validation.the_given_data_was_invalid' })
                );
            }
        } finally {
            if (requestId === requestIdRef.current) {
                setLoading(false);
                setDisplayTable(true);
            }
        }
    };

    const handleTableChange = (
        newPagination: TablePaginationConfig,
        filters: Record<string, (string | number | boolean)[] | null>,
        sorter: SorterResult<any> | SorterResult<any>[],
        _extra: TableCurrentDataSource<any>
    ) => {
        fetchData({ pagination: newPagination, sorter });
    };

    const handleCustomColumns = async (responseData: AnyRecord[]) => {
        const customColsDict: Record<string, { title: string; value: any }> = {};

        Object.values(responseData).forEach((row: AnyRecord) => {
            Object.entries(row).forEach(([key, value]) => {
                if (!defaultFields.includes(key)) {
                    customColsDict[key] = { title: key, value };
                }
            });
        });

        const parsedCustomColumns = Object.values(customColsDict).map((element) => ({
            title: element.title,
            dataIndex: element.title,
            key: element.title,
            sorter: true,
            render: (_text: string, record: AnyRecord) => {
                const cell = record[element.title];
                if (element.value?.type === 'DATE') {
                    return cell ? moment(cell).format(config.defaultDateFormat) : null;
                }
                if (cell && element.value?.type === 'SWITCH') {
                    return record[element.title]?.value ? 'TRUE' : 'FALSE';
                }
                if (cell && typeof cell === 'object' && 'value' in cell) {
                    return cell.value;
                }
                return cell ?? null;
            },
        }));

        const base = handleColumnDisplay(columns);
        setTableColumns([...base, ...parsedCustomColumns]);
        setColumns?.([...base, ...parsedCustomColumns]);
    };

    const handleCustomFields = () => {
        if (!learningProgramCustomColumns || data === undefined) return;

        if (customFields.length === 0 || (Array.isArray(data) && data.length === 0)) return;

        const updated = [...handleColumnDisplay(columns)];
        let actionsCol: any | undefined;

        for (let i = 0; i < updated.length; i++) {
            if (updated[i]?.key === 'actions') {
                actionsCol = updated[i];
                updated.splice(i, 1);
                break;
            }
        }

        customFields.forEach((field) => {
            Object.entries((data as AnyRecord[])[0]).forEach(([propKey]) => {
                if (field.title !== propKey && generalHelpers.camelize(field.title) !== propKey)
                    return;

                const column: any = {
                    title: field.title,
                    sorter: hasCustomSorter,
                    key: field.uuid,
                    render: (record: AnyRecord) =>
                        record[propKey] !== null && record[propKey] !== undefined
                            ? record[propKey]
                            : '-',
                };

                switch (field.field_type) {
                    case 'SWITCH':
                        column.render = (record: AnyRecord) => (
                            <Switch disabled defaultChecked={!!record[propKey]} />
                        );
                        break;
                    case 'DATE':
                        column.render = (record: AnyRecord) =>
                            record[propKey]
                                ? moment(record[propKey]).format(config.defaultDateFormat)
                                : '-';
                        break;
                    case 'MULTIPLE_CHOICE':
                        column.render = (record: AnyRecord) => {
                            if (record[propKey]) {
                                try {
                                    const arr: string[] = JSON.parse(record[propKey]);
                                    return (
                                        <ul style={{ paddingLeft: 18, margin: 0 }}>
                                            {arr.map((v, idx) => (
                                                <li key={idx}>{v}</li>
                                            ))}
                                        </ul>
                                    );
                                } catch {
                                    return '-';
                                }
                            }
                            return '-';
                        };
                        break;
                    case 'HYPERLINK':
                        column.render = (record: AnyRecord) => {
                            if (record[propKey]) {
                                try {
                                    const params = JSON.parse(record[propKey]);
                                    return (
                                        <div
                                            className="hyperlink-tag pointer"
                                            onClick={() => window.open(params.hyperlink, '_blank')}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter')
                                                    window.open(params.hyperlink, '_blank');
                                            }}
                                        >
                                            {params.displayName}
                                        </div>
                                    );
                                } catch {
                                    return '-';
                                }
                            }
                            return '-';
                        };
                        break;
                    default:
                        break;
                }

                updated.push(column);
            });
        });

        if (actionsCol) updated.push(actionsCol);
        setTableColumns(updated);
    };

    useEffect(() => {
        if (!columns) return;

        setTableColumns((prev) => {
            const base = handleColumnDisplay(columns);
            const actionsCol = base.find((c: any) => c.key === 'actions');

            return [
                ...base.filter((c: any) => c.key !== 'actions'),
                ...prev.filter((c) => !base.some((d: any) => d.key === c.key)),
                ...(actionsCol ? [actionsCol] : []),
            ];
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columns, displayColumns]);

    useEffect(() => {
        handleCustomFields();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    useEffect(() => {
        if (!reload) return;

        fetchData({
            pagination: {
                current: 1,
                pageSize: pagination.pageSize ?? defaultPageSize,
            },
        });

        setSelectedRowKeys?.([]);

        setReload(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reload]);

    useEffect(() => {
        if (manualDataUpdate) setData(updateData);

        if (fetchOnLoad) {
            fetchData({ pagination });
        } else {
            setDisplayTable(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const tableLocale = useMemo(
        () => ({ emptyText: intl.formatMessage({ id: 'general.found_no_data' }) }),
        [intl]
    );

    return (
        <>
            {displayTable ? (
                <AntDTable
                    columns={tableColumns}
                    locale={tableLocale}
                    rowKey={typeof rowKey === 'function' ? rowKey : (record) => record[rowKey]}
                    dataSource={data}
                    onHeaderRow={() => ({
                        onClick: (event: React.MouseEvent) => {
                            // Dev-only convenience reload. It must not fire for the header's
                            // select-all checkbox: the reload reset the selection the moment it was
                            // made, so ticking select-all looked like it did nothing at all.
                            const target = event.target as HTMLElement | null;

                            if (target?.closest?.('.ant-table-selection-column, .ant-checkbox, input')) {
                                return;
                            }

                            if (config.api.environment === 'development' && setReload)
                                setReload(true);
                        },
                    })}
                    rowSelection={
                        rowSelection
                            ? {
                                  ...selectionProps,
                                  selectedRowKeys,
                                  onChange: onSelectChange,
                                  preserveSelectedRowKeys: true,
                              }
                            : undefined
                    }
                    pagination={pagination}
                    loading={isLoading || loading}
                    components={components || undefined}
                    onChange={handleTableChange}
                    {...restProps}
                />
            ) : (
                <div className="flex justify-center">
                    <Spin />
                </div>
            )}
        </>
    );
};

export default QTable;
